<template>
    <div class="pa-ui pa-page rma-page">
        <header class="pa-page-header">
            <router-link :to="basePath" class="pa-eyebrow">
                <v-icon small>mdi-chevron-left</v-icon>{{ isLegal ? "Documentos do Jurídico" : "Documentos do mês" }}
            </router-link>
            <div class="pa-page-header__row">
                <div>
                    <h1 class="pa-title">Histórico</h1>
                    <p class="pa-subtitle">
                        {{ isLegal
                            ? "Todos os períodos das solicitações do Jurídico. Cada solicitação, lembrete e envio de documento fica registrado com data e hora."
                            : "Todos os meses da fiscalização mensal. Cada solicitação, lembrete e envio de documento fica registrado com data e hora." }}
                    </p>
                </div>
            </div>
        </header>

        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-else-if="loadError" class="pa-card">
            <div class="pa-empty">
                <v-icon large>{{ loadError.blocked ? "mdi-lock-outline" : "mdi-alert-circle-outline" }}</v-icon>
                <div>{{ loadError.message }}</div>
                <v-btn v-if="!loadError.blocked" outlined small class="mt-4" @click="load">Tentar de novo</v-btn>
            </div>
        </div>

        <div v-else-if="!competences.length" class="pa-card">
            <div class="pa-empty">
                <v-icon large>mdi-calendar-month-outline</v-icon>
                <div class="rma-empty__title">Ainda não há meses registrados</div>
                <div>A primeira solicitação chega por e-mail no dia 1 do mês, com a lista de documentos e o prazo de envio.</div>
            </div>
        </div>

        <div v-else class="pa-table-card">
            <v-data-table :headers="headers" :items="competences" item-key="id" hide-default-footer disable-pagination disable-sort
                :mobile-breakpoint="1100" class="pa-clickable-rows" @click:row="open">
                <template v-slot:[`item.label`]="{ item }">
                    <div class="pa-cell-main">{{ item.label }}</div>
                </template>
                <template v-slot:[`item.due_date`]="{ item }">
                    <span class="rma-nowrap">{{ item.due_date_label || formatDate(item.due_date) }}</span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <div class="rma-badges">
                        <pa-badge :variant="statusOf(item).tone" :text="statusOf(item).label" />
                        <pa-badge v-if="item.overdue && item.status !== 'complete'" variant="danger" text="prazo encerrado" />
                    </div>
                    <div v-if="item.completed_at" class="pa-cell-sub">em {{ formatDateTime(item.completed_at) }}</div>
                </template>
                <template v-slot:[`item.counts`]="{ item }">
                    <div class="rma-nowrap">{{ countsOf(item).delivered }} de {{ countsOf(item).total }}</div>
                    <div class="pa-meter rma-meter"><div class="pa-meter__fill" :style="{ width: `${percent(countsOf(item))}%` }"></div></div>
                </template>
                <template v-slot:[`item.requested_at`]="{ item }">
                    <div class="rma-nowrap">{{ item.requested_at ? formatDateTime(item.requested_at) : "—" }}</div>
                    <div v-if="item.reminder_sent_at" class="pa-cell-sub">Lembrete em {{ formatDateTime(item.reminder_sent_at) }}</div>
                    <div v-if="item.last_overdue_sent_at" class="pa-cell-sub">Última cobrança em {{ formatDateTime(item.last_overdue_sent_at) }}</div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-btn small outlined :to="documentsRoute(item)" @click.stop>Abrir</v-btn>
                </template>
            </v-data-table>
        </div>

        <section v-if="!loading && !loadError && competences.length" class="pa-card mt-6">
            <header class="pa-card__header">
                <div>
                    <h3 class="pa-section-title">E-mails recebidos da Administração Judicial</h3>
                    <p class="pa-section-description">Solicitações, lembretes, cobranças e devoluções enviados aos representantes do caso.</p>
                </div>
            </header>
            <div class="pa-card__body--flush">
                <v-data-table :headers="noticeHeaders" :items="notices" item-key="id" disable-sort :items-per-page="10" :mobile-breakpoint="900"
                    no-data-text="Nenhum e-mail enviado ainda" :footer-props="{ itemsPerPageOptions: [10, 25, 50], itemsPerPageText: 'Itens por página:', pageText: '{0}–{1} de {2}' }">
                    <template v-slot:[`item.created_at`]="{ item }"><span class="rma-nowrap">{{ formatDateTime(item.created_at) }}</span></template>
                    <template v-slot:[`item.label`]="{ item }">
                        <div class="pa-cell-main">{{ item.label }}</div>
                        <div v-if="item.document" class="pa-cell-sub">{{ item.document }}</div>
                    </template>
                    <template v-slot:[`item.subject`]="{ item }"><span class="pa-cell-sub">{{ item.subject || "—" }}</span></template>
                </v-data-table>
            </div>
        </section>
    </div>
</template>

<script>
import axios from "axios";
import { eventHub } from "@/main";
import PaBadge from "@/components/plan-adhesion/PaBadge.vue";
import { competenceStatus, errorMessage, formatDate, formatDateTime, percent } from "@/components/rma/rma.util";
import "@/components/plan-adhesion/pa-ui.scss";

// Historico da fiscalizacao mensal: uma linha por competencia, com a situacao,
// o prazo, o andamento e as datas da solicitacao e das cobrancas; abaixo, os
// e-mails que a AJ mandou ao caso.
export default {
    name: "RmaHistory",

    components: { PaBadge },

    props: {
        // Area: "accounting" (fiscalizacao mensal) ou "legal" (Juridico), pela rota.
        track: { type: String, default: "accounting" },
    },

    computed: {
        isLegal() {
            return this.track === "legal";
        },
        basePath() {
            return this.isLegal ? "/juridico" : "/fiscalizacao-mensal";
        },
    },

    watch: {
        track() {
            this.load();
        },
    },

    data: () => ({
        loading: true,
        loadError: null,
        competences: [],
        headers: [
            { text: "Competência", value: "label" },
            { text: "Prazo", value: "due_date" },
            { text: "Situação", value: "status" },
            { text: "Documentos", value: "counts" },
            { text: "Solicitado em", value: "requested_at" },
            { text: "", value: "actions", align: "end", width: 90 },
        ],
        notices: [],
        noticeHeaders: [
            { text: "Data", value: "created_at", width: 150 },
            { text: "Competência", value: "competence_label", width: 150 },
            { text: "Tipo", value: "label" },
            { text: "Assunto", value: "subject" },
        ],
    }),

    created() {
        this.load();
        eventHub.$on("client-context-changed", this.load);
    },

    beforeDestroy() {
        eventHub.$off("client-context-changed", this.load);
    },

    methods: {
        formatDate,
        formatDateTime,
        percent,

        statusOf(row) {
            return competenceStatus(row.status);
        },

        countsOf(row) {
            return row.counts || { total: 0, delivered: 0, missing: 0 };
        },

        documentsRoute(row) {
            return { path: this.basePath, query: { competencia: String(row.id) } };
        },

        open(row) {
            this.$router.push(this.documentsRoute(row)).catch(() => {});
        },

        async load() {
            this.loading = true;
            this.loadError = null;
            try {
                const [result, notices] = await Promise.all([
                    axios.get("/api/client/rma/competences", { params: { track: this.track } }),
                    // A lista de e-mails e complementar: se falhar, o historico continua.
                    axios.get("/api/client/rma/notices", { params: { track: this.track } }).catch(() => null),
                ]);
                this.competences = result.data.content || [];
                this.notices = notices && notices.data ? notices.data.content || [] : [];
            } catch (error) {
                const status = error && error.response && error.response.status;
                this.competences = [];
                this.loadError = status === 403
                    ? { blocked: true, message: errorMessage(error, this.isLegal ? "As solicitações do Jurídico não estão ativas para este caso." : "A fiscalização mensal não está ativa para este caso.") }
                    : { blocked: false, message: errorMessage(error, "Não foi possível carregar o histórico.") };
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.rma-empty__title {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 4px;
}

.rma-nowrap {
    white-space: nowrap;
}

.rma-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 6px;
}

.rma-meter {
    width: 90px;
    max-width: 100%;
    margin-top: 4px;
}

// Na versao empilhada (telas menores) o v-data-table vira cartoes: alinha o
// conteudo a direita e deixa os textos quebrarem.
.pa-table-card ::v-deep .v-data-table__mobile-row__cell {
    text-align: right;
}

.pa-table-card ::v-deep .v-data-table__mobile-row__cell .rma-badges {
    justify-content: flex-end;
}

.pa-table-card ::v-deep .v-data-table__mobile-row__cell .rma-meter {
    margin-left: auto;
}

.pa-table-card ::v-deep .v-data-table__mobile-row {
    height: auto !important;
    min-height: 44px;
    padding-top: 8px;
    padding-bottom: 8px;
}

@media (max-width: 700px) {
    .rma-page {
        padding: 20px 16px 40px;
    }

    .rma-page .pa-title {
        font-size: 22px;
        line-height: 30px;
    }

    .rma-nowrap {
        white-space: normal;
    }
}
</style>
